import { useDeleteLifeArea } from 'features/life-areas';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DeleteLifeAreaDialog } from 'shared/dialogs/delete-life-area';
import { LifeArea } from 'shared/types/life-area';

import {
  DeleteLifeAreaContext,
  DeleteLifeAreaContextProps,
} from './delete-life-area-context';

export type DeleteLifeAreaProviderProps = {
  children: React.ReactNode;
};

export const DeleteLifeAreaProvider: React.FC<DeleteLifeAreaProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lifeAreas, setLifeAreas] = useState<LifeArea[]>([]);
  const [redirectPath, setRedirectPath] = useState<string>();
  const {
    submit: deleteLifeAreas,
    error,
    isSuccess,
    isLoading,
    retry,
    reset,
  } = useDeleteLifeArea();

  const openDialog = useCallback(
    (lifeArea: LifeArea | LifeArea[], path?: string) => {
      setLifeAreas(Array.isArray(lifeArea) ? lifeArea : [lifeArea]);
      setRedirectPath(path);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    reset();
    setLifeAreas([]);
    setRedirectPath(undefined);
  }, [reset]);

  const submit = useCallback(() => {
    !!lifeAreas.length && deleteLifeAreas(lifeAreas);
  }, [deleteLifeAreas, lifeAreas]);

  const value = useMemo<DeleteLifeAreaContextProps>(
    () => ({ onDeleteLifeArea: openDialog }),
    [openDialog],
  );

  // The dialog bolds this between two copy fragments, so a count reads as a name.
  const name =
    lifeAreas.length === 1
      ? lifeAreas[0].name
      : t('forms.delete-life-area.message.multiple.label', {
          count: lifeAreas.length,
        });

  useEffect(() => {
    if (isSuccess) {
      redirectPath && navigate(redirectPath);
      closeDialog();
    }
  }, [closeDialog, isSuccess, navigate, redirectPath]);

  return (
    <DeleteLifeAreaContext.Provider value={value}>
      {children}
      <DeleteLifeAreaDialog
        open={!!lifeAreas.length}
        name={name}
        onClose={closeDialog}
        onSubmit={submit}
        onRetry={retry}
        isLoading={isLoading}
        isError={!!error}
      />
    </DeleteLifeAreaContext.Provider>
  );
};
